import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button, message as antMessage, Spin, Typography, Avatar, Card } from 'antd';
import { 
  SendOutlined, 
  PaperClipOutlined, 
  CloseOutlined, 
  UserOutlined,
  CustomerServiceOutlined,
  ReloadOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileTextOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useTheme } from '../../theme/ThemeProvider';
import { chatService } from '../../services/chatService';
import { ChatMessage, Attachment, SenderType, ViewState } from '../../types/chat';
import { createSignalRService, getSignalRService } from '../../services/signalRService';
import { SIGNALR_HUB_URL } from '../../config';
import { httpService } from '../../services/httpService';
import './styles.css';

const { TextArea } = Input;
const { Text } = Typography;

interface TripChatPopupProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  tripTitle?: string;
}

const TripChatPopup: React.FC<TripChatPopupProps> = ({ isOpen, onClose, tripId, tripTitle }) => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const signalRServiceRef = useRef<ReturnType<typeof getSignalRService>>(null);
  // Track messages we sent to prevent duplicates when ReceiveMessage comes back
  // Key format: "content_timestamp_tripId" - used to match when our sent message comes back via SignalR
  const pendingSentMessagesRef = useRef<Set<string>>(new Set());

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize SignalR and load chat history when component opens
  useEffect(() => {
    if (isOpen && tripId) {
      initializeSignalR();
      loadChatHistory();
    } else if (!isOpen) {
      // Cleanup SignalR when component closes
      cleanupSignalR();
    }

    return () => {
      // Cleanup on unmount
      cleanupSignalR();
    };
  }, [isOpen, tripId]);

  // Initialize SignalR connection
  const initializeSignalR = async () => {
    console.log('🟦 [TripChatPopup] ===== initializeSignalR() called =====');
    console.log('🟦 [TripChatPopup] tripId:', tripId);
    console.log('🟦 [TripChatPopup] SIGNALR_HUB_URL:', SIGNALR_HUB_URL);
    
    try {
      const token = httpService.getToken();
      console.log('🟦 [TripChatPopup] Token available:', token ? `Yes (${token.substring(0, 20)}...)` : 'No');
      
      const service = createSignalRService(SIGNALR_HUB_URL, () => httpService.getToken());
      signalRServiceRef.current = service;
      console.log('🟦 [TripChatPopup] SignalR service created and stored in ref');

      // Set up callbacks
      console.log('🟦 [TripChatPopup] Setting up callbacks...');
      service.setCallbacks({
        onMessageReceived: (message: ChatMessage) => {
          console.log('🟦 [TripChatPopup] ===== onMessageReceived callback fired =====');
          console.log('🟦 [TripChatPopup] Received message:', JSON.stringify(message, null, 2));
          console.log('🟦 [TripChatPopup] Pending sent messages:', Array.from(pendingSentMessagesRef.current));
          
          setMessages((prev) => {
            console.log('🟦 [TripChatPopup] Previous messages count:', prev.length);
            
            // Strategy 1: Check by message ID (most reliable)
            if (message.id) {
              const duplicateById = prev.some((m) => m.id === message.id);
              if (duplicateById) {
                console.log('🟦 [TripChatPopup] Duplicate detected by ID, skipping:', message.id);
                return prev;
              }
            }
            
            // Strategy 2: Check if this is a message we just sent (check pending set)
            // When we send a message, we add it to pendingSentMessagesRef with key: "content_timestamp_tripId"
            // When ReceiveMessage comes back, we check if content matches any pending message
            const messageContent = message.content.trim();
            let isPendingMessage = false;
            const pendingKeys = Array.from(pendingSentMessagesRef.current);
            
            for (const pendingKey of pendingKeys) {
              // Pending key format: "content_timestamp_tripId"
              // Check if the pending key starts with this message content
              if (pendingKey.startsWith(messageContent + '_')) {
                console.log('🟦 [TripChatPopup] This matches a pending message we sent, removing from pending:', pendingKey);
                pendingSentMessagesRef.current.delete(pendingKey);
                isPendingMessage = true;
                // Continue checking all pending messages to clean up
              }
            }
            
            // Strategy 3: Check by content, sender, and timestamp (within 3 seconds tolerance)
            // This catches duplicates even if pending check didn't work (e.g., if timestamp changed)
            const messageTime = new Date(message.timestamp).getTime();
            const isDuplicate = prev.some((m) => {
              const mTime = new Date(m.timestamp).getTime();
              const timeDiff = Math.abs(messageTime - mTime);
              return (
                m.content.trim() === messageContent &&
                m.sender === message.sender &&
                timeDiff < 3000 // 3 seconds tolerance
              );
            });
            
            if (isDuplicate || isPendingMessage) {
              console.log('🟦 [TripChatPopup] Duplicate or pending message detected, skipping');
              console.log('🟦 [TripChatPopup] isDuplicate:', isDuplicate, 'isPendingMessage:', isPendingMessage);
              console.log('🟦 [TripChatPopup] ===== End onMessageReceived (duplicate/pending) =====');
              return prev;
            }
            
            const newMessages = [...prev, message];
            console.log('🟦 [TripChatPopup] Adding message, new count:', newMessages.length);
            console.log('🟦 [TripChatPopup] ===== End onMessageReceived =====');
            return newMessages;
          });
        },
        onConnected: () => {
          console.log('🟦 [TripChatPopup] onConnected callback fired');
          setIsConnected(true);
          console.log('🟦 [TripChatPopup] isConnected state set to true');
        },
        onDisconnected: () => {
          console.log('🟦 [TripChatPopup] onDisconnected callback fired');
          setIsConnected(false);
          console.log('🟦 [TripChatPopup] isConnected state set to false');
        },
        onError: (errorMsg: string) => {
          console.error('🟦 [TripChatPopup] onError callback fired:', errorMsg);
          antMessage.warning('اتصال چت قطع شد. در حال تلاش برای اتصال مجدد...');
        },
      });
      console.log('🟦 [TripChatPopup] Callbacks set successfully');

      // Start connection and join trip chat
      console.log('🟦 [TripChatPopup] Starting SignalR connection...');
      await service.start();
      console.log('🟦 [TripChatPopup] SignalR connection started');
      
      console.log('🟦 [TripChatPopup] Joining trip chat...');
      await service.joinTripChat(tripId);
      console.log('🟦 [TripChatPopup] Joined trip chat successfully');
      console.log('🟦 [TripChatPopup] ===== initializeSignalR() completed =====');
    } catch (error) {
      console.error('❌ [TripChatPopup] Failed to initialize SignalR:', error);
      console.error('❌ [TripChatPopup] Error details:', JSON.stringify(error, null, 2));
      console.error('❌ [TripChatPopup] Error stack:', error instanceof Error ? error.stack : 'No stack');
      setIsConnected(false);
      // Continue without SignalR - will use REST API fallback
    }
  };

  // Cleanup SignalR connection
  const cleanupSignalR = async () => {
    if (signalRServiceRef.current) {
      try {
        await signalRServiceRef.current.stop();
        signalRServiceRef.current = null;
        setIsConnected(false);
      } catch (error) {
        console.error('Error cleaning up SignalR:', error);
      }
    }
  };

  const loadChatHistory = async () => {
    setIsLoading(true);
    try {
      // Load chat messages from backend API
      const messages = await chatService.getChatMessages(tripId);
      setMessages(messages);
    } catch (error) {
      console.error('Error loading chat messages:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Send message via REST API (fallback)
  const sendViaRestAPI = async (
    messageContent: string,
    messageType: 'text' | 'file',
    attachments: Attachment[]
  ) => {
    try {
      const newMessage = await chatService.sendMessage(
        tripId,
        messageContent || '',
        messageType,
        attachments,
        SenderType.Passenger
      );

      // Add message to local state
      setMessages(prev => [...prev, newMessage]);

      // Clear input
      setCurrentMessage('');
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error sending message via REST API:', error);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim() && selectedFiles.length === 0) {
      return;
    }

    setIsSending(true);
    try {
      const attachments: Attachment[] = [];
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          try {
            const uploadResult = await chatService.uploadFile(file, tripId);
            attachments.push({
              fileName: file.name,
              fileUrl: uploadResult.fileUrl,
              thumbnailUrl: uploadResult.thumbnailUrl
            });
          } catch (error) {
            console.error('Error uploading file:', error);
            const errorMessage = error instanceof Error ? error.message : 'خطا در آپلود فایل';
            antMessage.error(`خطا در آپلود فایل ${file.name}: ${errorMessage}`);
            // Don't stop the entire process if one file fails
          }
        }
        
        // If no files were uploaded successfully, show error and return
        if (attachments.length === 0 && selectedFiles.length > 0) {
          antMessage.error('هیچ فایلی آپلود نشد. لطفا دوباره تلاش کنید.');
          setIsSending(false);
          return;
        }
      }

      // Prepare message content
      let messageContent = currentMessage.trim();
      if (!messageContent && attachments.length > 0) {
        // If no message text but has attachments, show attachment filenames
        messageContent = `Attachment: ${attachments.map(att => att.fileName).join(', ')}`;
      }

      const messageType = attachments.length > 0 ? 'file' : 'text';
      const apiAttachments = attachments.map(att => ({
        fileName: att.fileName,
        fileUrl: att.fileUrl
      }));

      // Try to send via SignalR first, fallback to REST API
      const signalRService = signalRServiceRef.current;
      if (signalRService && signalRService.isConnected()) {
        try {
          // Create a unique key for this message to track it
          // This helps us identify when the same message comes back via ReceiveMessage
          const messageKey = `${messageContent}_${Date.now()}_${tripId}`;
          pendingSentMessagesRef.current.add(messageKey);
          console.log('🟦 [TripChatPopup] Added to pending messages:', messageKey);
          console.log('🟦 [TripChatPopup] Total pending:', pendingSentMessagesRef.current.size);
          
          // Send via SignalR
          await signalRService.sendMessage(tripId, messageContent || '', messageType, apiAttachments);
          console.log('🟦 [TripChatPopup] Message sent via SignalR, waiting for ReceiveMessage callback');
          
          // Clear input immediately for better UX
          // The message will be added to state when ReceiveMessage callback fires
          setCurrentMessage('');
          setSelectedFiles([]);
          
          // Clean up pending message after 5 seconds if it wasn't received
          setTimeout(() => {
            if (pendingSentMessagesRef.current.has(messageKey)) {
              console.log('🟦 [TripChatPopup] Pending message timeout, removing:', messageKey);
              pendingSentMessagesRef.current.delete(messageKey);
            }
          }, 5000);
          
        } catch (signalRError) {
          console.error('❌ [TripChatPopup] SignalR send failed, falling back to REST API:', signalRError);
          // Remove from pending since we're using REST API fallback
          const messageKey = `${messageContent}_${Date.now()}_${tripId}`;
          pendingSentMessagesRef.current.delete(messageKey);
          // Fallback to REST API
          await sendViaRestAPI(messageContent, messageType, attachments);
        }
      } else {
        console.log('🟦 [TripChatPopup] SignalR not connected, using REST API');
        // Use REST API if SignalR is not connected
        await sendViaRestAPI(messageContent, messageType, attachments);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      antMessage.error('خطا در ارسال پیام');
    } finally {
      setIsSending(false);
    }
  };

  // Default thumbnail when none exists or loading fails
  const DEFAULT_THUMB =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyMCIgaGVpZ2h0PSIxODAiIGZpbGw9IiNlZWVlZWUiLz48cGF0aCBkPSJNODAgMTI1bDQwLTUwaDQwbDUwIDcwSDgwWiIgZmlsbD0iI2NjY2NjYyIvPjxyZWN0IHg9IjgwIiB5PSI1MCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMTIiIGZpbGw9IiNkZGQiLz48L3N2Zz4=';

  // Max file size (10 MB) and allowed file types
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_TYPES = [
    // Images
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    // Documents
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    // Text files
    'text/plain',
    'text/csv',
  ];

  const isFileAllowed = (file: File): boolean => {
    return ALLOWED_TYPES.includes(file.type);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    // Validate each file
    const validFiles: File[] = [];
    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        antMessage.error(`حجم فایل ${file.name} بیشتر از ۱۰ مگابایت است.`);
        return;
      }
      if (isFileAllowed(file)) {
        validFiles.push(file);
      } else {
        antMessage.error(`نوع فایل ${file.name} مجاز نیست. فقط فایل‌های تصویری، PDF و Office مجاز هستند.`);
      }
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIconByName = (fileName: string) => {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    const commonStyle = { fontSize: 40, color: '#777' } as const;
    if (['pdf'].includes(ext)) return <FilePdfOutlined style={commonStyle} />;
    if (['doc', 'docx'].includes(ext)) return <FileWordOutlined style={commonStyle} />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined style={commonStyle} />;
    if (['ppt', 'pptx'].includes(ext)) return <FilePptOutlined style={commonStyle} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FileZipOutlined style={commonStyle} />;
    if (['txt', 'md', 'rtf'].includes(ext)) return <FileTextOutlined style={commonStyle} />;
    return <FileOutlined style={commonStyle} />;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CustomerServiceOutlined style={{ color: theme.primary }} />
            <span>چت پشتیبانی - {tripTitle || `سفر ${tripId}`}</span>
          </div>
          <Button
            type="text"
            onClick={loadChatHistory}
            loading={isLoading}
            aria-label="Refresh chat"
            icon={<ReloadOutlined />}
          />
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="chat-popup-modal"
      style={{
        top: 20,
        maxHeight: '90vh'
      }}
    >
      <div className="chat-container" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
        {/* Connection Status */}
        <div
          style={{
            padding: '8px 16px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: 500,
            backgroundColor: isConnected ? '#4caf50' : '#f44336',
            color: 'white',
            borderRadius: '4px',
            marginBottom: '8px'
          }}
        >
          {isConnected ? '🟢 متصل - پیام‌ها به صورت لحظه‌ای دریافت می‌شوند' : '🔴 قطع - در حال اتصال...'}
        </div>

        {/* Messages Area */}
        <div className="messages-container" style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '16px',
          backgroundColor: theme.background,
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>در حال بارگذاری پیامها...</div>
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: theme.text.secondary }}>
              <CustomerServiceOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>هنوز پیامی برای این سفر ارسال نشده است</div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                برای شروع گفتگو، پیام خود را بنویسید
              </div>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={msg.id || `${msg.timestamp}-${msg.sender}-${index}`}
                className={`message ${msg.senderType === SenderType.Passenger ? 'user-message' : 'support-message'}`}
                style={{
                  marginBottom: '16px',
                  display: 'flex',
                  flexDirection: msg.senderType === SenderType.Passenger ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}
              >
                <Avatar
                  icon={msg.senderType === SenderType.Passenger ? <UserOutlined /> : <CustomerServiceOutlined />}
                  style={{
                    backgroundColor: msg.senderType === SenderType.Passenger ? theme.primary : theme.success,
                    flexShrink: 0
                  }}
                />
                <div style={{ maxWidth: '70%' }}>
                  <Card
                    size="small"
                    style={{
                      backgroundColor: msg.senderType === SenderType.Passenger ? theme.primary : theme.background,
                      color: '#777',
                      border: 'none'
                    }}
                  >
                    <div style={{ color:'#777', marginBottom: msg.attachments.length > 0 ? '8px' : '0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.content}
                    </div>
                    {msg.attachments.length > 0 && (
                      <div>
                        {msg.attachments.map((attachment, attIndex) => {
                          const isImage = attachment.fileName.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                          
                          if (isImage) {
                            // Use thumbnail if exists, else original; fallback to default if both missing or fail
                            const initialSrc = attachment.thumbnailUrl || attachment.fileUrl || DEFAULT_THUMB;
                            return (
                              <div key={attIndex} style={{ marginTop: '8px' }}>
                                <img 
                                  src={initialSrc} 
                                  alt={attachment.fileName}
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                  onError={(e) => {
                                    const currentSrc = e.currentTarget.src;
                                    const thumbSrc = attachment.thumbnailUrl || '';
                                    const originalSrc = attachment.fileUrl;
                                    // If thumbnail failed and original exists, try original once
                                    if (thumbSrc && currentSrc.includes(thumbSrc) && originalSrc) {
                                      e.currentTarget.src = originalSrc;
                                      return;
                                    }
                                    // Otherwise fallback to default placeholder
                                    if (currentSrc !== DEFAULT_THUMB) {
                                      e.currentTarget.src = DEFAULT_THUMB;
                                    }
                                  }}
                                  onClick={() => window.open(attachment.fileUrl, '_blank')}
                                />
                                {/* Removed filename hyperlink under images */}
                              </div>
                            );
                          } else {
                            return (
                              <div key={attIndex} style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div>
                                  {getFileIconByName(attachment.fileName)}
                                </div>
                                <a 
                                  href={attachment.fileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: '#177ddc',
                                    textDecoration: 'underline',
                                    marginTop: '6px',
                                    fontSize: '12px',
                                    textAlign: 'center',
                                    maxWidth: '100%',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {attachment.fileName}
                                </a>
                              </div>
                            );
                          }
                        })}
                      </div>
                    )}
                  </Card>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px', 
                    color: theme.text.secondary, 
                    marginTop: '4px',
                    justifyContent: msg.senderType === SenderType.Passenger ? 'flex-end' : 'flex-start'
                  }}>
                    <span>{formatTimestamp(msg.timestamp)}</span>
                    <span>
                      {msg.viewState === ViewState.None && (
                        <CheckOutlined style={{ color: '#999' }} />
                      )}
                      {msg.viewState === ViewState.Send && (
                        <CheckOutlined style={{ color: theme.primary }} />
                      )}
                      {msg.viewState === ViewState.Viewed && (
                        <>
                          <CheckOutlined style={{ color: theme.primary, marginRight: 2 }} />
                          <CheckOutlined style={{ color: theme.primary }} />
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Text strong>فایلهای انتخاب شده:</Text>
            <div style={{ marginTop: '8px' }}>
              {selectedFiles.map((file, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 8px',
                  backgroundColor: theme.background,
                  borderRadius: '4px',
                  marginBottom: '4px'
                }}>
                  <FileOutlined />
                  <span style={{ flex: 1, fontSize: '14px' }}>{file.name}</span>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => removeFile(index)}
                    style={{ color: theme.error }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="input-container">
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              style={{ flexShrink: 0 }}
            >
              فایل
            </Button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextArea
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              placeholder="پیام خود را بنویسید..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ flex: 1 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={isSending}
              disabled={!currentMessage.trim() && selectedFiles.length === 0}
              style={{ flexShrink: 0 }}
            >
              ارسال
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TripChatPopup;
